import { useRef, useState, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Typography,
  Spin,
  Divider,
  Modal,
  Input,
  notification,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { journalsQueries, JournalRecordPageShell } from "@/entities/journal";
import {
  $journalApi,
  useJournalMutation,
  journalQueryOptions,
} from "@/entities/journal/fetch/fetch-client";
import { useLocalUser, useCoordinator } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { JournalSigner, JournalRecordInfo } from "@/shared/ui";
import { NOTIFICATION_MESSAGES, useUnifiedAutoResize } from "@/shared/utils";
import { useAppRoutes } from "@shared/lib/react-router";
import { getSectionOneActions } from "../../model/actions-map";
import { useBuildSignLine } from "../../model/mutate-hooks/use-build-sign-line";
import { useWithSignedString } from "../../model/utils/with-signed-string";
import {
  SectionFirstActions,
  SectionFirstDetails,
} from "../journal-section-info/ui";
import { useGetItemsVersion } from "../journals-section-first/journal-version-modal/model/use-get-items-version";
import { JournalVersionModal } from "../journals-section-first/journal-version-modal/ui";
import "./journal-section-info-page.scss";

const { Title } = Typography;

export const JournalSectionInfoPage = () => {
  const navigate = useNavigate();
  const { itemNoVersionId } = useParams<{ itemNoVersionId: string }>();
  const { mainJournalId, projectId, objectId, titleId } = useCoordinator();
  const appRoutes = useAppRoutes();

  const [historyOpen, setHistoryOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [signingString, setSigningString] = useState<string>("");
  const localUser = useLocalUser();

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const sectionOneId = mainJournalData?.mainJournal?.section1?.sectionId || "";
  const sectionOneListId = mainJournalData?.mainJournal?.section1?.sectionListId || "";

  const {
    data: currentItem,
    isFetching,
    isPending,
    error,
  } = useQuery({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-one/items/{itemNoVersionId}",
      {
        params: {
          path: { itemNoVersionId: itemNoVersionId || "" },
        },
      },
    ),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnReconnect: true,
    enabled: !!itemNoVersionId,
  });

  const { data: versions } = useGetItemsVersion(sectionOneListId, itemNoVersionId);

  const handleClose = () => {
    navigate(
      appRoutes.journals.section1(projectId, objectId, mainJournalId, titleId),
    );
  };

  const { mutateAsync: sendToSign } = useJournalMutation(
    "post",
    "/api/v1/journals/section-one/{sectionOneListId}/items/{itemNoVersionId}/sendToSignItem",
  );
  const { mutateAsync: recallSign } = useJournalMutation(
    "post",
    "/api/v1/journals/section-one/{sectionOneListId}/items/{itemNoVersionId}/status/inputterReturn",
  );
  const { mutateAsync: returnToWork } = useJournalMutation(
    "post",
    "/api/v1/journals/section-one/{sectionOneListId}/items/{itemNoVersionId}/return",
  );
  const { mutateAsync: deleteItem } = useJournalMutation(
    "delete",
    "/api/v1/journals/section-one/items/{itemNoVersionId}",
  );
  const { mutateAsync: rejectSign } = useJournalMutation(
    "post",
    "/api/v1/journals/section-one/{sectionOneListId}/items/{itemNoVersionId}/status/signerRejected",
  );
  const { mutateAsync: getSigningXml, isPending: isSigningPending } =
    useBuildSignLine();
  const { withSignedString } = useWithSignedString();

  const invalidateItem = () => {
    queryClient.invalidateQueries(
      journalQueryOptions(
        "get",
        "/api/v1/journals/section-one/items/{itemNoVersionId}",
        { params: { path: { itemNoVersionId: itemNoVersionId || "" } } },
      ),
    );
    if (!sectionOneId) return;
    const listKey = journalsQueries.getSection1({ sectionOneId }).queryKey;
    queryClient.invalidateQueries({ queryKey: [listKey[0], listKey[1]] });
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  useUnifiedAutoResize(wrapperRef as RefObject<HTMLElement>);

  if (
    error &&
    (error.code === 500 || error.code === 403 || error.code === 404)
  ) {
    handleClose();
    return null;
  }

  if (!currentItem) return null;

  const signerFio = currentItem?.signer
    ? `${currentItem.signer.surname ?? ""} ${currentItem.signer.firstName?.charAt(0) ?? ""}. ${currentItem.signer.middleName?.charAt(0) ?? ""}.`.trim()
    : "-";

  const actions = getSectionOneActions(
    currentItem?.status,
    !!(currentItem as any)?.versionId,
  );
  const canSend = actions.includes("sendToSign");
  const canReject = actions.includes("rejectSign");
  const canRollback = actions.includes("rollback");
  const canEdit = actions.includes("edit");
  const canDelete = actions.includes("delete");
  const canVersions = actions.includes("versions");

  const handleSend = async () => {
    if (!sectionOneListId || !itemNoVersionId) return;
    await sendToSign({
      params: { path: { sectionOneListId, itemNoVersionId } },
    });
    invalidateItem();
  };

  const handleRecall = async () => {
    if (!sectionOneListId || !itemNoVersionId) return;
    await recallSign({
      params: { path: { sectionOneListId, itemNoVersionId } },
    });
    invalidateItem();
  };

  const handleRollback = async () => {
    if (!sectionOneListId || !itemNoVersionId) return;
    await returnToWork({
      params: { path: { sectionOneListId, itemNoVersionId } },
    });
    invalidateItem();
  };

  const handleDelete = async () => {
    if (!itemNoVersionId) return;
    await deleteItem({ params: { path: { itemNoVersionId } } });
    invalidateItem();
    handleClose();
  };

  const handleEditClick = () => {
    if (!itemNoVersionId) return;
    navigate(
      appRoutes.journals.section1Form(projectId, objectId, mainJournalId, titleId, itemNoVersionId),
    );
  };

  const isSignerAwaiting =
    currentItem?.status === "AWAITING_SIGNATURE" &&
    currentItem?.signer?.userId === localUser?.user_id;

  const openReject = () => setRejectOpen(true);
  const closeReject = () => {
    setRejectOpen(false);
    setRejectComment("");
    setRejectError("");
  };

  const confirmReject = async () => {
    if (!rejectComment.trim()) {
      setRejectError("Укажите причину отклонения");
      return;
    }

    try {
      if (!sectionOneListId || !itemNoVersionId) return;
      await rejectSign({
        params: { path: { sectionOneListId, itemNoVersionId } },
        body: {
          signer: { signerEmployeeId: localUser?.user_id || "" },
          comment: rejectComment,
        },
      });
      notification.success({ message: NOTIFICATION_MESSAGES.ENTRY_RECALLED_SUCCESS }); // "Запись отозвана"
      invalidateItem();
    } catch (e) {
      notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_REJECT_ERROR }); // "Ошибка при отклонении"
    } finally {
      closeReject();
    }
  };

  const currentVersion = versions?.section1List && versions.section1List.length > 0
    ? versions.section1List.reduce((latest: any, current: any) => {
        if (!latest) return current;
        return (current.versionNumber || 0) > (latest.versionNumber || 0) ? current : latest;
      }, null)
    : null;
  const currentVersionNumber = currentVersion?.versionNumber;
  const currentVersionSigner = currentVersion?.signer;
  const currentVersionDate = currentVersion?.updateDate;

  return (
    <JournalRecordPageShell ref={wrapperRef} onClose={handleClose}>
        <Spin spinning={isFetching || isPending}>
          <div className="journal-section-info-page-header">
            <Title level={3} style={{ marginBottom: 0 }}>
              Раздел № 1. Просмотр записи номер №{currentItem?.sequenceNumber}
            </Title>

            <JournalRecordInfo
              updateDate={currentItem?.updateDate}
              updateUserId={currentItem?.updateUserId}
              status={currentItem?.status}
              statusUpdateDate={currentVersionDate || currentItem?.updateDate}
              currentVersionId={currentVersion ? currentItem?.itemNoVersionHistoryId : undefined}
              currentVersionNumber={currentVersionNumber}
              signerInfo={currentVersionSigner || currentItem?.signer}
            />
          </div>

          <div>
            <SectionFirstDetails currentItem={currentItem} />

            <JournalSigner
              title="Подписант"
              signerLabel="Уполномоченный представитель лица, осуществляющего строительство"
              currentItem={currentItem}
              signerFio={signerFio}
              isSignerAwaiting={isSignerAwaiting}
              sectionListId={sectionOneListId}
              itemNoVersionId={itemNoVersionId || ""}
              localUser={localUser}
              signingString={signingString}
              isSigningPending={isSigningPending}
              getSigningXml={getSigningXml}
              setSigningString={setSigningString}
              withSignedString={withSignedString}
              invalidateItem={invalidateItem}
              openReject={openReject}
              canAction="section_1"
            />

            <Divider style={{ margin: "24px 0 16px" }} />
            <SectionFirstActions
              canSend={canSend}
              canReject={canReject}
              canRollback={canRollback}
              canDelete={canDelete}
              canVersions={canVersions}
              canEdit={canEdit}
              handleSend={handleSend}
              handleRecall={handleRecall}
              handleRollback={handleRollback}
              handleDelete={handleDelete}
              setHistoryOpen={setHistoryOpen}
              handleEditClick={handleEditClick}
              handleClose={handleClose}
            />
          </div>

          <Modal
            title="Отклонить подписание"
            open={rejectOpen}
            onCancel={closeReject}
            onOk={confirmReject}
            okText="Отклонить"
            cancelText="Отмена"
          >
            <Input.TextArea
              rows={4}
              placeholder="Комментарий"
              value={rejectComment}
              onChange={(e: any) => {
                setRejectComment(e.target.value);
                if (e.target.value.trim()) {
                  setRejectError("");
                }
              }}
              status={rejectError ? "error" : ""}
              maxLength={255}
              showCount
            />
            {rejectError && (
              <div className="journal-form-error--ant">
                {rejectError}
              </div>
            )}
          </Modal>

          {canVersions && (
            <JournalVersionModal
              open={historyOpen}
              onCancel={() => setHistoryOpen(false)}
              section1ListId={sectionOneListId}
              itemNoVersionId={itemNoVersionId || ""}
            />
          )}
        </Spin>
    </JournalRecordPageShell>
  );
};
